/**
 * Construction point for every NSwag-generated client.
 *
 * Two defects in the generated code make a direct `new XClient()` wrong, and both are fixed here:
 *
 *  1. Every generated class does `this.baseUrl = baseUrl ?? "https://localhost:45623"`. Call sites
 *     used to construct with no argument, so all generated traffic went absolute to a dev port and
 *     broke everywhere else. We pass "" — this SPA is always same-origin with its API (CORS in
 *     Program.cs has no AllowAnyMethod and the auth cookie is SameSite=Strict, so a separate origin
 *     is not viable anyway).
 *
 *  2. Every generated class does `this.instance = instance || axios.create()`. A fresh instance does
 *     not carry the 401 response interceptor registered in general/HttpClient, so an expired session
 *     on a generated call never redirected to sign-in. We pass the shared, configured axios.
 *
 * APIClient.ts is generated — regenerate it (`npm run api`), never hand-edit it. When a regeneration
 * adds a controller, add its client here; nothing else in the app constructs one.
 */

import type { AxiosInstance } from "axios";
import http from "../general/HttpClient";
import * as Api from "./APIClient";

type GeneratedClient<T> = new (baseUrl?: string, instance?: AxiosInstance) => T;

const make = <T>(Client: GeneratedClient<T>): T => new Client("", http as unknown as AxiosInstance);

// -------------------------------------------------------------------- account
export const accountClient = make(Api.AccountClient);

// ------------------------------------------------- hierarchy (RPC-style APIs)
// These answer HTTP 200 with `success: false` on failure — always route their
// responses through `unwrap()` / `unwrapVoid()` from ./errors.
export const structureClient = make(Api.AssessmentStructureClient);
export const managementClient = make(Api.AssessmentManagementClient);
export const assessmentEnrollmentClient = make(Api.AssessmentEnrollmentClient);
export const enrollmentClient = make(Api.EnrollmentClient);

// ------------------------------------------ activities, marks and vocabulary
export const activitiesClient = make(Api.AssessmentActivitiesClient);
export const assessmentTypesClient = make(Api.AssessmentTypesClient);
export const commentsClient = make(Api.CommentsClient);
export const manualMarksClient = make(Api.ManualMarksClient);
export const markEntrySheetClient = make(Api.MarkEntrySheetClient);
export const markWorkflowClient = make(Api.MarkWorkflowClient);
export const markGridClient = make(Api.MarkGridClient);

// ------------------------------------------------------------------- grading
export const gradeScalesClient = make(Api.GradeScalesClient);

// ------------------------------------------------- procedures and calculation
export const calculationProceduresClient = make(Api.CalculationProceduresClient);
export const procedureAssignmentsClient = make(Api.ProcedureAssignmentsClient);
export const calculationsClient = make(Api.CalculationsClient);
export const calculationFindingsClient = make(Api.CalculationFindingsClient);

// ----------------------------------------------- reporting, audit, platform
export const reportsClient = make(Api.ReportsClient);
export const auditClient = make(Api.AuditClient);
export const integrationsClient = make(Api.IntegrationsClient);
export const scadsysIntegrationClient = make(Api.ScadsysIntegrationClient);
export const immersiveReaderClient = make(Api.ImmersiveReaderClient);
